/**
 * Price Comparison Table page (default landing page).
 *
 * Fetches comparison data from GET /api/comparison and renders the
 * virtualised PriceComparisonTable with a FilterPanel above it.
 *
 * Manages:
 *   - Filter state (limit, search, competitors, stock filters)
 *   - Sort state persisted to localStorage
 *   - CSV export via the export endpoint
 *
 * Per-domain staleness indicators are provided by the shared
 * `useDataFreshness` hook (context set up in Layout.jsx).
 *
 * Session 5.3 delivered the core table rendering.
 * Session 5.4 adds filters, sort, export, and staleness.
 * Session 5.11 migrated freshness polling to the shared hook.
 *
 * Spec reference: §6.8 (Frontend: Price Comparison Table Tab).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getComparison, exportComparison } from '../utils/api';
import { useDataFreshness } from '../hooks/useDataFreshness';
import { useIsMobile } from '../hooks/useMediaQuery';
import FilterPanel from '../components/FilterPanel';
import MobileComparisonList from '../components/MobileComparisonList';
import PriceComparisonTable from '../components/PriceComparisonTable';
import {
  comparisonCacheKey,
  getCachedComparison,
  hasCachedComparison,
  setCachedComparison,
} from '../utils/comparisonCache';

// ---------------------------------------------------------------------------
// Query params helper
// ---------------------------------------------------------------------------

/** Build the API query params object from filter + sort state. */
function _buildParams(filters, sortBy, sortDir) {
  const params = {
    limit: filters.limit,
    sort_by: sortBy,
    sort_dir: sortDir,
  };
  if (filters.search) params.search = filters.search;
  if (filters.competitors) params.competitors = filters.competitors;
  if (filters.key_lines) params.key_lines = true;
  if (filters.our_in_stock) params.our_in_stock = true;
  if (filters.competitor_in_stock) params.competitor_in_stock = true;
  if (filters.competitor_oos) params.competitor_oos = true;
  if (filters.competitor_has_price) params.competitor_has_price = true;
  if (filters.has_errors) {
    params.has_errors = true;
    if (filters.error_reasons) params.error_reasons = filters.error_reasons;
  }
  return params;
}

// ---------------------------------------------------------------------------
// localStorage keys for sort persistence
// ---------------------------------------------------------------------------
const LS_SORT_BY = 'comparison_sort_by';
const LS_SORT_DIR = 'comparison_sort_dir';
const LS_LIMIT = 'comparison_limit';
const LS_PRODUCT_WIDTH = 'comparison_product_width';

/** Valid values for the "Show" selector — must match FilterPanel's LIMIT_OPTIONS. */
const VALID_LIMITS = new Set(['10', '50', '100', '200', '400', 'all']);

/** Restore the saved "Show" limit, falling back to 100 for unknown values. */
function _initialLimit() {
  const saved = localStorage.getItem(LS_LIMIT);
  return saved && VALID_LIMITS.has(saved) ? saved : '100';
}

/** sessionStorage keys for filter memory (shared with TrendPage). */
const SS_REMEMBER_COMP = 'comparison_remember_competitors';
const SS_COMPETITOR_FILTER = 'comparison_competitor_filter';
const SS_STOCK_FILTERS = 'comparison_stock_filters';
const SS_SEARCH = 'comparison_search';

/** Default product column width (px) — wide enough for full product names. */
const PRODUCT_WIDTH_DEFAULT = 500;

/** Minimum product column width (px) — matches COL.productMin in the table. */
const PRODUCT_WIDTH_MIN = 160;

/** Restore the saved product column width, falling back to the default. */
function _initialProductWidth() {
  const saved = parseInt(localStorage.getItem(LS_PRODUCT_WIDTH), 10);
  return Number.isFinite(saved) && saved >= PRODUCT_WIDTH_MIN
    ? saved
    : PRODUCT_WIDTH_DEFAULT;
}

export default function ComparisonPage() {
  // Below md the sticky-column table cannot fit (941px of frozen
  // columns); mount the card list instead. Conditional mount, not CSS
  // hiding — a hidden table would still render ~900 cells and its
  // scroll-memory effects would clobber saved offsets.
  const isMobile = useIsMobile();

  // ------ Product column width (persisted so it survives navigation) ------
  const [productWidth, setProductWidth] = useState(_initialProductWidth);

  useEffect(() => {
    localStorage.setItem(LS_PRODUCT_WIDTH, String(productWidth));
  }, [productWidth]);

  // ------ Filter state ------
  const [filters, setFilters] = useState(() => {
    const remembered = sessionStorage.getItem(SS_REMEMBER_COMP) === 'true';
    let savedCompetitors;
    let savedStock = {};
    if (remembered) {
      savedCompetitors = sessionStorage.getItem(SS_COMPETITOR_FILTER) || undefined;
      try {
        const raw = sessionStorage.getItem(SS_STOCK_FILTERS);
        if (raw) savedStock = JSON.parse(raw);
      } catch { /* ignore */ }
    }
    const savedSearch = sessionStorage.getItem(SS_SEARCH) || undefined;
    return {
      limit: _initialLimit(),
      search: savedSearch,
      competitors: savedCompetitors,
      key_lines: savedStock.key_lines || false,
      our_in_stock: savedStock.our_in_stock || false,
      competitor_in_stock: savedStock.competitor_in_stock || false,
      competitor_oos: savedStock.competitor_oos || false,
      competitor_has_price: savedStock.competitor_has_price || false,
      has_errors: savedStock.has_errors || false,
      error_reasons: savedStock.error_reasons || undefined,
    };
  });

  // ------ Sort state (persisted to localStorage) ------
  const [sortBy, setSortBy] = useState(
    () => localStorage.getItem(LS_SORT_BY) || 'product_name',
  );
  const [sortDir, setSortDir] = useState(
    () => localStorage.getItem(LS_SORT_DIR) || 'asc',
  );

  // ------ Data state (seeded from cache so tab returns render instantly) ------
  const [data, setData] = useState(
    () => getCachedComparison(comparisonCacheKey(_buildParams(filters, sortBy, sortDir))),
  );
  const [loading, setLoading] = useState(
    () => !hasCachedComparison(comparisonCacheKey(_buildParams(filters, sortBy, sortDir))),
  );
  const [error, setError] = useState('');

  // ------ All competitors (captured from initial fetch) ------
  const allCompetitorsRef = useRef([]);
  const [allCompetitors, setAllCompetitors] = useState([]);

  // ------ Freshness (shared context from Layout) ------
  const { freshness } = useDataFreshness();

  // ------ AbortController for request cancellation ------
  const abortRef = useRef(null);

  // ------ Export state ------
  const [exporting, setExporting] = useState(false);

  // ------ Remember competitor filter ------
  const [rememberCompetitors, setRememberCompetitors] = useState(
    () => sessionStorage.getItem(SS_REMEMBER_COMP) === 'true',
  );

  // ======================================================================
  // Data fetching
  // ======================================================================

  /**
   * Build the query params object from current filter + sort state.
   */
  const buildParams = useCallback(
    () => _buildParams(filters, sortBy, sortDir),
    [filters, sortBy, sortDir],
  );

  /**
   * Fetch comparison data from the backend API.
   *
   * Cancels any in-flight request before starting a new one to prevent
   * stale responses from overwriting fresh data (race condition).
   */
  const fetchData = useCallback(async () => {
    // Cancel previous in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    const params = buildParams();
    const key = comparisonCacheKey(params);
    const cached = getCachedComparison(key);

    setError('');
    if (cached) {
      // Stale-while-revalidate: show cached data immediately (no spinner)
      // and refresh in the background.
      setData(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    try {
      const result = await getComparison(params, controller.signal);

      // If this request was cancelled while in-flight, ignore the result
      if (controller.signal.aborted) return;

      setCachedComparison(key, result);
      setData(result);

      // Capture the full competitor list — grow it as we see new domains
      if (result.competitors.length > 0) {
        const existing = new Set(allCompetitorsRef.current);
        let changed = false;
        for (const c of result.competitors) {
          if (!existing.has(c)) {
            existing.add(c);
            changed = true;
          }
        }
        if (allCompetitorsRef.current.length === 0 || changed) {
          const sorted = [...existing].sort();
          allCompetitorsRef.current = sorted;
          setAllCompetitors(sorted);
        }
      }
    } catch (err) {
      // Ignore abort errors — they are expected when cancelling stale requests
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || controller.signal.aborted) {
        return;
      }
      // If we're showing cached data, a failed background revalidation should
      // not replace it with an error message.
      if (!cached) {
        const detail = err.response?.data?.detail;
        setError(detail || 'Failed to load comparison data. Please try again.');
      }
    } finally {
      // Only clear loading if this is still the active request
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [buildParams]);

  // On mount, fetch full competitor list with an unfiltered request if needed
  const didFetchAllRef = useRef(false);
  useEffect(() => {
    if (didFetchAllRef.current) return;
    if (!filters.competitors) return; // unfiltered, main fetch will capture them
    didFetchAllRef.current = true;
    // Fire a lightweight fetch (limit=10, no filters) to discover all competitors
    getComparison({ limit: '10', sort_by: 'product_name', sort_dir: 'asc' })
      .then((result) => {
        if (result.competitors?.length > 0) {
          allCompetitorsRef.current = result.competitors;
          setAllCompetitors(result.competitors);
        }
      })
      .catch(() => { /* non-critical */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when filters or sort change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ======================================================================
  // Sort handler (persists to localStorage)
  // ======================================================================

  const handleSort = useCallback(
    (sortKey) => {
      if (sortBy === sortKey) {
        const newDir = sortDir === 'asc' ? 'desc' : 'asc';
        setSortDir(newDir);
        localStorage.setItem(LS_SORT_DIR, newDir);
      } else {
        setSortBy(sortKey);
        setSortDir('asc');
        localStorage.setItem(LS_SORT_BY, sortKey);
        localStorage.setItem(LS_SORT_DIR, 'asc');
      }
    },
    [sortBy, sortDir],
  );

  // Explicit (key, dir) setter for the mobile card list, whose sort
  // control is a select + direction toggle rather than column headers.
  const handleSortSet = useCallback((sortKey, dir) => {
    setSortBy(sortKey);
    setSortDir(dir);
    localStorage.setItem(LS_SORT_BY, sortKey);
    localStorage.setItem(LS_SORT_DIR, dir);
  }, []);

  // ======================================================================
  // Remember competitor filter
  // ======================================================================

  // Persist the "Show" limit to localStorage so it survives navigation
  useEffect(() => {
    localStorage.setItem(LS_LIMIT, filters.limit);
  }, [filters.limit]);

  // Persist search to sessionStorage so it survives navigation to trend pages
  useEffect(() => {
    if (filters.search) {
      sessionStorage.setItem(SS_SEARCH, filters.search);
    } else {
      sessionStorage.removeItem(SS_SEARCH);
    }
  }, [filters.search]);

  /** The remembered stock/flag filters, as stored under "Remember filters". */
  const stockFilterBlob = useMemo(() => JSON.stringify({
    key_lines: filters.key_lines || false,
    our_in_stock: filters.our_in_stock || false,
    competitor_in_stock: filters.competitor_in_stock || false,
    competitor_oos: filters.competitor_oos || false,
    competitor_has_price: filters.competitor_has_price || false,
    has_errors: filters.has_errors || false,
    error_reasons: filters.error_reasons || undefined,
  }), [filters.key_lines, filters.our_in_stock, filters.competitor_in_stock, filters.competitor_oos, filters.competitor_has_price, filters.has_errors, filters.error_reasons]);

  // Persist filters to sessionStorage when they change
  useEffect(() => {
    if (rememberCompetitors) {
      if (filters.competitors) {
        sessionStorage.setItem(SS_COMPETITOR_FILTER, filters.competitors);
      } else {
        sessionStorage.removeItem(SS_COMPETITOR_FILTER);
      }
      sessionStorage.setItem(SS_STOCK_FILTERS, stockFilterBlob);
    }
  }, [filters.competitors, stockFilterBlob, rememberCompetitors]);

  const handleRememberCompetitors = useCallback((checked) => {
    setRememberCompetitors(checked);
    sessionStorage.setItem(SS_REMEMBER_COMP, String(checked));
    if (!checked) {
      sessionStorage.removeItem(SS_COMPETITOR_FILTER);
      sessionStorage.removeItem(SS_STOCK_FILTERS);
    } else {
      if (filters.competitors) {
        sessionStorage.setItem(SS_COMPETITOR_FILTER, filters.competitors);
      }
      sessionStorage.setItem(SS_STOCK_FILTERS, stockFilterBlob);
    }
  }, [filters.competitors, stockFilterBlob]);

  // ======================================================================
  // Filter handler
  // ======================================================================

  const handleFilterChange = useCallback((partial) => {
    setFilters((prev) => ({ ...prev, ...partial }));
  }, []);

  // ======================================================================
  // CSV export
  // ======================================================================

  const handleExport = useCallback(async (historyDays = null) => {
    setExporting(true);
    try {
      // Same params as the table query, but every row (limit='all')
      const params = _buildParams({ ...filters, limit: 'all' }, sortBy, sortDir);
      if (historyDays) params.history_days = historyDays;

      const blob = await exportComparison(params);

      // Trigger browser download. The file name says when it holds key
      // lines only, so the two kinds of export are easy to tell apart.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const scope = filters.key_lines ? 'key-lines-' : '';
      const range = historyDays ? `${historyDays}d-` : '';
      a.download = `price-comparison-${scope}${range}${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [filters, sortBy, sortDir]);

  // ======================================================================
  // Enrich rows with _incomplete flag
  // ======================================================================

  const enrichedRows = useMemo(() => {
    if (!data?.rows) return [];
    const totalComp = allCompetitorsRef.current.length || 0;
    if (totalComp === 0) return data.rows;
    return data.rows.map((row) => ({
      ...row,
      _incomplete:
        Object.keys(row.competitor_prices || {}).length < totalComp,
    }));
  }, [data]);

  // ======================================================================
  // Filter competitors to only those selected in the dropdown
  // ======================================================================

  const filteredCompetitors = useMemo(() => {
    if (!data?.competitors) return [];
    if (!filters.competitors) return data.competitors;
    const selected = new Set(filters.competitors.split(',').filter(Boolean));
    return data.competitors.filter((c) => selected.has(c));
  }, [data?.competitors, filters.competitors]);

  // ======================================================================
  // Render
  // ======================================================================

  return (
    // Fill the Layout shell's <main> (flex column) instead of the old
    // calc(100vh - 7.5rem): the shell is dvh-sized, so this tracks the
    // real visible viewport on mobile URL-bar collapse too.
    <div className="flex flex-col flex-1 min-h-0 min-w-0">
      {/* Filter panel — always visible */}
      <FilterPanel
        filters={filters}
        allCompetitors={allCompetitors}
        onFilterChange={handleFilterChange}
        onExport={handleExport}
        exporting={exporting}
        rememberCompetitors={rememberCompetitors}
        onRememberCompetitorsChange={handleRememberCompetitors}
        keyLineCount={data?.key_line_count}
        onOurPricesRefreshed={fetchData}
      />

      {/* Loading state */}
      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-3 text-gray-400">
            <svg
              className="h-5 w-5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2
                   5.291A7.962 7.962 0 014 12H0c0 3.042 1.135
                   5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm">Loading comparison data&hellip;</span>
          </div>
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <div
            role="alert"
            className="flex items-center gap-2 text-red-600 text-sm"
          >
            <svg
              className="h-5 w-5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18
                   0Zm-9 3.75h.008v.008H12v-.008Z"
              />
            </svg>
            <span>{error}</span>
          </div>
          <button
            type="button"
            onClick={fetchData}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium
                       text-white shadow-sm hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && enrichedRows.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center">
          <svg
            className="h-12 w-12 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0
                 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504
                 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125
                 1.125-1.125m18.375 2.625V5.625m0 12.75c0
                 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0
                 0h-9.75m10.875-9h-17.25M3.375
                 5.625c0-.621.504-1.125 1.125-1.125h13.5c.621 0
                 1.125.504 1.125 1.125m-16.875 0v1.5c0
                 .621.504 1.125 1.125 1.125m16.875-2.625v1.5c0
                 .621-.504 1.125-1.125 1.125M6 9h12"
            />
          </svg>
          <h2 className="mt-3 text-lg font-medium text-gray-700">
            {filters.key_lines && data?.key_line_count === 0
              ? 'No key lines defined yet'
              : 'No products found'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {filters.key_lines && data?.key_line_count === 0
              ? 'Add a "Key Lines" column to the Master Scraper sheet, mark '
                + 'the key products with Y and upload it on the Upload tab.'
              : 'Price data has not been ingested yet, or no products match the '
                + 'current filters.'}
          </p>
        </div>
      )}

      {/* Table (desktop) or card list (mobile) */}
      {!loading && !error && enrichedRows.length > 0 && (
        isMobile ? (
          <MobileComparisonList
            rows={enrichedRows}
            competitors={filteredCompetitors}
            totalCount={data.total_count}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortSet={handleSortSet}
          />
        ) : (
          <PriceComparisonTable
            rows={enrichedRows}
            competitors={filteredCompetitors}
            totalCount={data.total_count}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            freshness={freshness}
            filters={filters}
            productWidth={productWidth}
            onProductWidthChange={setProductWidth}
          />
        )
      )}
    </div>
  );
}
